import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  Shirt,
  Utensils,
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  RefreshCw,
} from "lucide-react";

const SIZE_OPTIONS = ["all", "1 ano", "2 anos", "4 anos", "6 anos", "8 anos", "10 anos", "PP", "P", "M", "G", "GG", "XG", "XGG", "EXG", "EXGG"];

const KIND_OPTIONS = [
  { key: "all", label: "Todos" },
  { key: "shirt", label: "Só camisetas" },
  { key: "lunch", label: "Só almoços" },
];

const STATUS_OPTIONS = [
  { key: "all", label: "Todos" },
  { key: "paid", label: "Pagos" },
  { key: "pending", label: "Pendentes" },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function StatCard({ title, icon: Icon, bought, picked, breakdownBought, breakdownPicked, breakdownLabel }) {
  const remaining = Math.max(0, bought - picked);
  const percent = bought > 0 ? Math.round((picked / bought) * 100) : 0;
  return (
    <Card className="border-stone-200 rounded-2xl overflow-hidden card-warm-shadow">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-700 to-orange-600 flex items-center justify-center">
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{title}</div>
              <div className="font-display text-2xl font-bold text-stone-900">
                {picked} <span className="text-stone-400 text-lg font-normal">/ {bought}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-stone-500">Pendentes</div>
            <div className="font-display text-xl font-bold text-orange-700">{remaining}</div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-stone-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="text-[11px] text-stone-500 mt-1">{percent}% retirado</div>

        {Object.keys(breakdownBought || {}).length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-100">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">
              Por {breakdownLabel}
            </div>
            <div className="space-y-1.5">
              {Object.entries(breakdownBought)
                .sort((a, b) => (b[1] - a[1]))
                .map(([key, total]) => {
                  const done = (breakdownPicked || {})[key] || 0;
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                  return (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      <div className="w-16 truncate font-medium text-stone-700">{key || "—"}</div>
                      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-14 text-right tabular-nums text-stone-600">
                        {done}/{total}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ManagePickups() {
  const { user } = useAuth();
  const isStaff = !!user;
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [nameInputs, setNameInputs] = useState({}); // {rowId:field: "name"}
  const [filters, setFilters] = useState({
    q: "",
    date_from: "",
    date_to: "",
    size: "all",
    status: "paid",
    kind: "all",
  });

  const buildParams = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "all") p[k] = v;
    });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/pickups", { params: buildParams() }),
        api.get("/pickups/stats"),
      ]);
      setRows(list || []);
      setStats(st || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao carregar retiradas");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const togglePickup = async (row, field, next) => {
    const key = `${row.id}:${field}`;
    const nameKey = `${row.id}:${field}`;
    const byName = next ? (nameInputs[nameKey] || "").trim() : "";
    setSaving((s) => ({ ...s, [key]: true }));
    // Optimistic update
    setRows((rs) =>
      rs.map((r) =>
        r.id === row.id
          ? {
              ...r,
              [field]: {
                done: next,
                at: next ? new Date().toISOString() : "",
                by: next ? (user?.email || "") : "",
                by_name: byName,
              },
            }
          : r
      )
    );
    try {
      const key2 = field === "pickup_shirt" ? "shirt" : "lunch";
      await api.patch(`/pickups/${row.id}`, { [key2]: next, picked_up_by_name: byName });
      toast.success(next ? (byName ? `Retirada registrada — ${byName}` : "Retirada registrada") : "Retirada desmarcada");
      // clear the name input after successful save
      if (!next) {
        setNameInputs((n) => {
          const nn = { ...n };
          delete nn[nameKey];
          return nn;
        });
      }
      // Refresh stats after a change (light call)
      api.get("/pickups/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch (e) {
      // rollback
      setRows((rs) =>
        rs.map((r) =>
          r.id === row.id ? { ...r, [field]: row[field] } : r
        )
      );
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao atualizar");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const download = async (fmt) => {
    try {
      const resp = await api.get(`/pickups.${fmt}`, {
        params: buildParams(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `controle-retiradas-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erro ao exportar");
    }
  };

  const clearFilters = () => {
    setFilters({ q: "", date_from: "", date_to: "", size: "all", status: "paid", kind: "all" });
  };

  const hasActiveFilters = useMemo(() => {
    return filters.q || filters.date_from || filters.date_to || filters.size !== "all" || filters.status !== "paid" || filters.kind !== "all";
  }, [filters]);

  return (
    <div className="space-y-8" data-testid="manage-pickups">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Controle</div>
          <h1 className="font-display text-4xl font-bold mt-2">Retiradas</h1>
          <p className="text-stone-600 mt-2">Marque a entrega de camisetas e a retirada de almoços no dia do evento.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => load()}
            disabled={loading}
            className="rounded-full"
            data-testid="pickups-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button
            variant="outline"
            onClick={() => download("xlsx")}
            className="rounded-full"
            data-testid="pickups-export-xlsx"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => download("pdf")}
            className="rounded-full"
            data-testid="pickups-export-pdf"
          >
            <FileText className="h-4 w-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Retirada rápida removida: use os filtros abaixo */}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pickups-stats">
        <StatCard
          title="Camisetas"
          icon={Shirt}
          bought={stats?.shirts?.bought || 0}
          picked={stats?.shirts?.picked || 0}
          breakdownBought={stats?.shirts?.by_size_bought}
          breakdownPicked={stats?.shirts?.by_size_picked}
          breakdownLabel="tamanho"
        />
        <StatCard
          title="Almoços"
          icon={Utensils}
          bought={stats?.lunches?.bought || 0}
          picked={stats?.lunches?.picked || 0}
          breakdownBought={stats?.lunches?.by_variant_bought}
          breakdownPicked={stats?.lunches?.by_variant_picked}
          breakdownLabel="cardápio"
        />
      </div>

      {/* Filters */}
      <Card className="border-stone-200 rounded-2xl">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-xs">Buscar (nome, email, telefone, cidade, comprovante)</Label>
              <div className="relative mt-1">
                <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  placeholder="Digite para buscar..."
                  className="pl-9"
                  data-testid="pickups-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Data inicial</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="mt-1"
                data-testid="pickups-date-from"
              />
            </div>
            <div>
              <Label className="text-xs">Data final</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="mt-1"
                data-testid="pickups-date-to"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Tamanho de camiseta</Label>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="pickups-size-filter">
                {SIZE_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFilters({ ...filters, size: s })}
                    className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-all ${
                      filters.size === s
                        ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-sm"
                        : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                    }`}
                  >
                    {s === "all" ? "Todos" : s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {KIND_OPTIONS.map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => setFilters({ ...filters, kind: k.key })}
                    className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-all ${
                      filters.kind === k.key
                        ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-sm"
                        : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                    }`}
                    data-testid={`pickups-kind-${k.key}`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Status do pedido</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setFilters({ ...filters, status: s.key })}
                    className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-all ${
                      filters.status === s.key
                        ? "bg-gradient-to-r from-red-700 to-orange-600 text-white border-transparent shadow-sm"
                        : "bg-white text-stone-700 border-stone-300 hover:border-orange-500 hover:text-orange-700"
                    }`}
                    data-testid={`pickups-status-${s.key}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-stone-600"
                data-testid="pickups-clear-filters"
              >
                Limpar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rows */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed border-stone-300 rounded-2xl">
          <CardContent className="py-16 text-center">
            <CheckCircle2 className="h-10 w-10 text-stone-400 mx-auto" />
            <div className="font-display text-xl mt-4 text-stone-700">Nenhum pedido encontrado</div>
            <p className="text-stone-500 text-sm mt-2">Ajuste os filtros ou verifique se há pedidos com camisetas/almoço no período.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-stone-600 px-1">
            <span className="font-semibold text-stone-900">{rows.length}</span> pedido(s) na lista
          </div>
          <div className="space-y-2" data-testid="pickups-list">
            {rows.map((r) => {
              const shirtDone = r.pickup_shirt?.done;
              const lunchDone = r.pickup_lunch?.done;
              const savingShirt = saving[`${r.id}:pickup_shirt`];
              const savingLunch = saving[`${r.id}:pickup_lunch`];
              const shirtNameKey = `${r.id}:pickup_shirt`;
              const lunchNameKey = `${r.id}:pickup_lunch`;
              const shirtNameVal = nameInputs[shirtNameKey] ?? "";
              const lunchNameVal = nameInputs[lunchNameKey] ?? "";
              return (
                <Card
                  key={r.id}
                  className="border-stone-200 rounded-2xl overflow-hidden"
                  data-testid={`pickup-row-${r.id}`}
                >
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr] gap-4">
                      {/* Customer */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-display font-semibold text-stone-900 truncate">{r.customer_name}</div>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                            r.status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                            r.status === "pending" ? "bg-amber-100 text-amber-800 border-amber-200" :
                            "bg-stone-200 text-stone-600 border-stone-300"
                          }`}>
                            {r.status === "paid" ? "Pago" : r.status === "pending" ? "Pendente" : "Cancelado"}
                          </span>
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5 truncate">{r.customer_email}</div>
                        <div className="text-xs text-stone-500 truncate">{r.customer_phone}{r.city ? ` · ${r.city}` : ""}</div>
                        {r.txid && (
                          <div className="text-[10px] font-mono text-stone-400 mt-1 truncate">{r.txid}</div>
                        )}
                        <div className="text-[11px] text-stone-400 mt-1">{formatDate(r.created_at)}</div>
                      </div>

                      {/* Shirt */}
                      <div className={`rounded-xl border p-3 ${r.has_shirt ? (shirtDone ? "bg-emerald-50 border-emerald-200" : "bg-stone-50 border-stone-200") : "bg-stone-50/50 border-dashed border-stone-200 opacity-60"}`}>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-stone-700">
                          <Shirt className="h-3.5 w-3.5" /> Camiseta
                        </div>
                        {r.has_shirt ? (
                          <>
                            <div className="text-sm text-stone-800 mt-1.5 space-y-0.5">
                              {r.shirt_labels.map((l, i) => (
                                <div key={i}>{l}</div>
                              ))}
                            </div>
                            {!shirtDone && r.status === "paid" && (
                              <div className="mt-2">
                                <Input
                                  value={shirtNameVal}
                                  onChange={(e) => setNameInputs((n) => ({ ...n, [shirtNameKey]: e.target.value }))}
                                  placeholder="Quem retirou (opcional)"
                                  className="h-8 text-xs"
                                  data-testid={`pickup-shirt-name-${r.id}`}
                                />
                              </div>
                            )}
                            <div className="mt-3">
                              <Button
                                size="sm"
                                variant={shirtDone ? "outline" : "default"}
                                disabled={!isStaff || savingShirt || r.status !== "paid"}
                                onClick={() => togglePickup(r, "pickup_shirt", !shirtDone)}
                                className={`w-full rounded-full ${shirtDone ? "border-emerald-300 text-emerald-800 hover:bg-emerald-100" : "bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white"}`}
                                data-testid={`toggle-pickup-shirt-${r.id}`}
                              >
                                {savingShirt ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : shirtDone ? (
                                  <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Retirada · desfazer</>
                                ) : (
                                  <>Marcar entrega</>
                                )}
                              </Button>
                              {shirtDone && r.pickup_shirt?.at && (
                                <div className="text-[10px] text-emerald-800 mt-1.5 text-center leading-tight">
                                  <div>{formatDate(r.pickup_shirt.at)}</div>
                                  {r.pickup_shirt.by_name ? (
                                    <div className="font-semibold">Retirou: {r.pickup_shirt.by_name}</div>
                                  ) : null}
                                  {r.pickup_shirt.by ? <div className="text-emerald-700/70">{r.pickup_shirt.by}</div> : null}
                                </div>
                              )}
                              {r.status !== "paid" && (
                                <div className="text-[10px] text-amber-700 mt-1.5 text-center">
                                  Aguardando confirmação de pagamento
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-stone-400 mt-2">Sem camiseta neste pedido</div>
                        )}
                      </div>

                      {/* Lunch */}
                      <div className={`rounded-xl border p-3 ${r.has_lunch ? (lunchDone ? "bg-emerald-50 border-emerald-200" : "bg-stone-50 border-stone-200") : "bg-stone-50/50 border-dashed border-stone-200 opacity-60"}`}>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-stone-700">
                          <Utensils className="h-3.5 w-3.5" /> Almoço
                        </div>
                        {r.has_lunch ? (
                          <>
                            <div className="text-sm text-stone-800 mt-1.5 space-y-0.5">
                              {r.lunch_labels.map((l, i) => (
                                <div key={i}>{l}</div>
                              ))}
                            </div>
                            {!lunchDone && r.status === "paid" && (
                              <div className="mt-2">
                                <Input
                                  value={lunchNameVal}
                                  onChange={(e) => setNameInputs((n) => ({ ...n, [lunchNameKey]: e.target.value }))}
                                  placeholder="Quem retirou (opcional)"
                                  className="h-8 text-xs"
                                  data-testid={`pickup-lunch-name-${r.id}`}
                                />
                              </div>
                            )}
                            <div className="mt-3">
                              <Button
                                size="sm"
                                variant={lunchDone ? "outline" : "default"}
                                disabled={!isStaff || savingLunch || r.status !== "paid"}
                                onClick={() => togglePickup(r, "pickup_lunch", !lunchDone)}
                                className={`w-full rounded-full ${lunchDone ? "border-emerald-300 text-emerald-800 hover:bg-emerald-100" : "bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white"}`}
                                data-testid={`toggle-pickup-lunch-${r.id}`}
                              >
                                {savingLunch ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : lunchDone ? (
                                  <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Retirado · desfazer</>
                                ) : (
                                  <>Marcar retirada</>
                                )}
                              </Button>
                              {lunchDone && r.pickup_lunch?.at && (
                                <div className="text-[10px] text-emerald-800 mt-1.5 text-center leading-tight">
                                  <div>{formatDate(r.pickup_lunch.at)}</div>
                                  {r.pickup_lunch.by_name ? (
                                    <div className="font-semibold">Retirou: {r.pickup_lunch.by_name}</div>
                                  ) : null}
                                  {r.pickup_lunch.by ? <div className="text-emerald-700/70">{r.pickup_lunch.by}</div> : null}
                                </div>
                              )}
                              {r.status !== "paid" && (
                                <div className="text-[10px] text-amber-700 mt-1.5 text-center">
                                  Aguardando confirmação de pagamento
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-stone-400 mt-2">Sem almoço neste pedido</div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
